// Eager callback route: the SDK handles the code before isLoading becomes false.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { apiOrigin } from '../api/apiOrigin';
import { sitevars } from '../config/sitevars';
import { AuthFailure } from './ProtectedRoute';
import { useLocalPasswordSession } from './localPasswordSession';

function Auth0RedirectLogin() {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const [redirectFailed, setRedirectFailed] = useState(false);
  const redirectStarted = useRef(false);
  const params = new URLSearchParams(window.location.search);
  const callbackPresent = params.has('code') || params.has('error') || params.has('state');
  const signIn = () => {
    setRedirectFailed(false);
    void loginWithRedirect({ appState: { returnTo: '/accounts' } })
      .catch(() => setRedirectFailed(true));
  };
  useEffect(() => {
    if (isLoading || isAuthenticated || error || callbackPresent || redirectStarted.current) return;
    redirectStarted.current = true;
    void loginWithRedirect({ appState: { returnTo: '/accounts' } })
      .catch(() => setRedirectFailed(true));
  }, [isLoading, isAuthenticated, error, callbackPresent, loginWithRedirect]);

  if (error || redirectFailed || (!isLoading && !isAuthenticated && callbackPresent)) {
    return <AuthFailure onRetry={signIn} />;
  }
  if (!isLoading && isAuthenticated) return <Navigate to="/accounts" replace />;
  return <main className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
    <h1 tabIndex={-1} className="text-lg font-medium text-muted-foreground">Signing you in…</h1>
  </main>;
}

function LocalPasswordLogin() {
  const navigate = useNavigate();
  const session = useLocalPasswordSession();
  const auth0Configured = Boolean(sitevars.auth0Domain && sitevars.auth0ClientId && sitevars.auth0Audience);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failed, setFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFailed(false);
    setSubmitting(true);
    try {
      const response = await fetch(`${apiOrigin}/api/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password }),
      });
      if (!response.ok) {
        setFailed(true);
        return;
      }
      const body = await response.json() as { access_token?: string };
      if (!body.access_token || !session) {
        setFailed(true);
        return;
      }
      session.setToken(body.access_token);
      navigate('/accounts', { replace: true });
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 p-6">
      <div className="w-full max-w-sm space-y-4">
        <h1 className="text-xl font-medium">Local test sign-in</h1>
        <p className="text-sm text-muted-foreground">
          This form is only available on a local development build. It is not the production Auth0 sign-in.
        </p>
        <form className="space-y-3" onSubmit={(event) => { void onSubmit(event); }}>
          <label className="block text-sm">
            Email
            <input
              className="mt-1 w-full rounded border px-3 py-2"
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label className="block text-sm">
            Password
            <input
              className="mt-1 w-full rounded border px-3 py-2"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {failed ? <p role="alert" className="text-sm text-destructive">We couldn’t verify your sign-in</p> : null}
          <button type="submit" className="rounded border px-4 py-2" disabled={submitting}>
            Sign in
          </button>
        </form>
        {auth0Configured ? <GoogleSignInButton /> : null}
      </div>
    </main>
  );
}

function GoogleSignInButton() {
  const { loginWithRedirect } = useAuth0();
  return (
    <button
      type="button"
      className="rounded border px-4 py-2"
      onClick={() => { void loginWithRedirect({ appState: { returnTo: '/accounts' } }); }}
    >
      Continue with Google
    </button>
  );
}

export function LoginPage() {
  if (sitevars.localPasswordAuth) return <LocalPasswordLogin />;
  return <Auth0RedirectLogin />;
}
export default LoginPage;
